import clsx from "clsx";
import { isEqual } from "lodash-es";
import React from "react";

import { GenericDropdown, Icon, IconName } from "halifax";
import {
  APPLIED_PREMIUM_SEARCH_FILTER,
  AppliedPremiumSearchFilterProperties,
  PremiumStaysFilterType,
} from "redmond";

import { trackEvent } from "../../../../../../api/v0/analytics/trackEvent";
import { VacationRentalsRoomCounts, initialState } from "../../../../reducer";
import * as textConstants from "../../textConstants";
import { RoomCountSelection } from "../RoomCountSelection";

export interface RoomCountFilterProps {
  roomCounts: VacationRentalsRoomCounts;
  setVacationRentalsRoomCounts: (counts: VacationRentalsRoomCounts) => void;
  appliedFilterTrackingProperties: Omit<
    AppliedPremiumSearchFilterProperties,
    "filter_type"
  >;
}

export const RoomCountFilter = (props: RoomCountFilterProps) => {
  const {
    roomCounts,
    setVacationRentalsRoomCounts,
    appliedFilterTrackingProperties,
  } = props;

  const [localCounts, setLocalCounts] =
    React.useState<VacationRentalsRoomCounts>(roomCounts);
  const localRoomCountsSet = !isEqual(roomCounts, localCounts);
  const resetLocalRoomCounts = () => {
    setLocalCounts(roomCounts);
  };

  const [closeCountsPopover, setCloseCountsPopover] =
    React.useState<boolean>(false);

  const roomCountsSet = !isEqual(
    roomCounts,
    initialState.vacationRentalsRoomCounts
  );
  const resetRoomCounts = () => {
    setVacationRentalsRoomCounts(initialState.vacationRentalsRoomCounts);

    // Track reset with reset values
    trackEvent({
      eventName: APPLIED_PREMIUM_SEARCH_FILTER,
      properties: {
        ...appliedFilterTrackingProperties,
        filter_type: PremiumStaysFilterType.Bedrooms,
        bedrooms: initialState.vacationRentalsRoomCounts.min_bedrooms,
        beds: initialState.vacationRentalsRoomCounts.min_beds,
        bathrooms: initialState.vacationRentalsRoomCounts.min_bathrooms,
      },
    });
  };
  const handleApplyRoomCounts = () => {
    setVacationRentalsRoomCounts(localCounts);

    // Track apply with new values
    trackEvent({
      eventName: APPLIED_PREMIUM_SEARCH_FILTER,
      properties: {
        ...appliedFilterTrackingProperties,
        filter_type: PremiumStaysFilterType.Bedrooms,
        bedrooms: localCounts.min_bedrooms,
        beds: localCounts.min_beds,
        bathrooms: localCounts.min_bathrooms,
      },
    });

    setCloseCountsPopover(true);
  };

  return (
    <GenericDropdown
      buttonClassName={clsx(
        "premier-collection-availability-dropdown",
        "rounded",
        "b2b-shop-filter",
        { "has-value": roomCountsSet }
      )}
      popoverClassName={clsx(
        "vacation-rentals-availability-room-counts-dropdown-popover",
        "b2b"
      )}
      ariaLabel={`${textConstants.ROOM_COUNT_FILTER_BUTTON_TEXT} filter`}
      dropdownLabel={
        <strong>{textConstants.ROOM_COUNT_FILTER_BUTTON_TEXT}</strong>
      }
      dropdownIcon={
        roomCountsSet ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              resetRoomCounts();
            }}
          >
            <Icon name={IconName.XCircle} />
          </div>
        ) : undefined
      }
      dropdownContent={
        <RoomCountSelection
          counts={localCounts}
          setCounts={setLocalCounts}
          onClickApply={handleApplyRoomCounts}
          hasValueToApply={localRoomCountsSet}
        />
      }
      showClose={false}
      onToggle={(open) => {
        resetLocalRoomCounts();
        setCloseCountsPopover(!open);
      }}
      forceClose={closeCountsPopover}
    />
  );
};
